/**
 * ReadyToFirePanel — Step 1 Plan Wave "before you pick a wave type" strip.
 *
 * Replaces the old `NextMovePanel + TrendsPanel` duo that the brand rep
 * reported as "doing nothing for me and adding no value." This panel is a
 * single, decision-focused card offering 2–3 **pre-wired Wave launches**
 * based on the brand's footprint + live wave telemetry.
 *
 * Each Ready-to-Fire card shows:
 *   • a concrete brand-specific thesis ("Glowmart footfall is up 14% this week
 *     in your NYC+Miami stores — mirror Wave Augusta's 6.4× ROAS run")
 *   • projected reach × ROAS (lifted from recent same-vertical live waves)
 *   • creator count + specific burst/role/angle preset
 *   • a single `Use this → Plan Wave` button that applies the preset to
 *     Step 1 state so the rep can tweak + fire within a minute
 *
 * Data source: a new lightweight endpoint `/api/brand/ready-to-fire` that
 * hand-assembles these from the brand's top live wave + footprint signals.
 * If the endpoint is empty or 4xx, we render three stable investor-demo
 * fixtures — demo never looks broken.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Zap, Flame, Target, TrendingUp, ChevronRight, Loader2,
} from 'lucide-react';
import { useDemoProfile } from '../../hooks/useDemoProfile';

const API = process.env.REACT_APP_BACKEND_URL;

// Investor-demo fallback so the panel is always populated in the pitch.
const DEMO_READY_FIRE = [
  {
    id: 'retail-footfall',
    icon: 'retail',
    title: 'Mirror Aurora Ritual v2',
    thesis: 'Glowmart foot traffic +14% in your NYC + Miami stores this week. Aurora Ritual v2 hit 6.4× ROAS on this exact geo triplet 11 days ago.',
    creator_count: 18, burst_count: 3,
    projected_reach: '2.1M', projected_roas: '5.8×',
    goal: 'Conversion',
    preset: {
      wave_type: 'Performance',
      goal: 'Conversion',
      platforms: ['TikTok', 'Instagram'],
      geo_level: 'dma',
      selected_dmas: ['New York', 'Miami'],
      commission_pct: 15,
      primary_kpi: 'ROAS',
    },
  },
  {
    id: 'competitor-whitespace',
    icon: 'whitespace',
    title: 'Hit the Dermaline whitespace',
    thesis: 'Competitor Dermaline has zero TikTok activity for 17 days. Your top 8 creators\' audiences overlap 62% with Dermaline followers.',
    creator_count: 12, burst_count: 2,
    projected_reach: '1.4M', projected_roas: '4.1×',
    goal: 'Awareness',
    preset: {
      wave_type: 'Branding',
      goal: 'Awareness',
      platforms: ['TikTok'],
      primary_kpi: 'Reach',
    },
  },
  {
    id: 'trend-velocity',
    icon: 'trend',
    title: '#HelloAura hook is accelerating',
    thesis: '"Hello Aura" sound has 2.3× velocity vs last week. 4 of your contracted creators have already used it organically.',
    creator_count: 24, burst_count: 4,
    projected_reach: '3.7M', projected_roas: '4.9×',
    goal: 'Awareness',
    preset: {
      wave_type: 'Branding',
      goal: 'Awareness',
      platforms: ['TikTok', 'Instagram'],
      primary_kpi: 'Reach',
    },
  },
];

const ICON_BY_KIND = {
  retail: Target,
  whitespace: Flame,
  trend: TrendingUp,
};

export default function ReadyToFirePanel({ onApplyPreset }) {
  const [items, setItems] = useState(null);
  const { profile, isDemo } = useDemoProfile();

  useEffect(() => {
    // Demo brands receive brand-specific Ready-to-Fire cards from the
    // /api/brand/demo-profile payload. Falls back to the generic backend
    // endpoint, and finally the wellness fixtures if neither is available.
    if (isDemo && profile?.ready_to_fire?.length) {
      setItems(profile.ready_to_fire);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/api/brand/ready-to-fire`, { withCredentials: true })
      .then(r => { if (!cancelled && r.data?.items?.length) setItems(r.data.items); })
      .catch(() => { /* fallthrough to fixtures */ })
      .finally(() => { if (!cancelled) setItems(prev => prev || DEMO_READY_FIRE); });
    return () => { cancelled = true; };
  }, [isDemo, profile]);

  return (
    <section className="rounded-2xl bg-[#0F0F0F] border border-[#1f1f1f] overflow-hidden" data-testid="ready-to-fire-panel">
      <header className="px-5 py-3 border-b border-[#1f1f1f] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-[#FF4D94]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF4D94] font-bold">Ready to fire</p>
        </div>
        <p className="text-[10px] text-gray-500">AI-pre-wired from your footprint + live signals · tap to pre-fill Step 1</p>
      </header>

      {!items ? (
        <div className="px-5 py-6 flex items-center gap-2 text-gray-500 text-xs">
          <Loader2 className="h-4 w-4 animate-spin" /> Assembling brand-specific waves…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1f1f1f]">
          {items.map(item => (
            <ReadyCard key={item.id} item={item} onApply={onApplyPreset} />
          ))}
        </div>
      )}
    </section>
  );
}

function ReadyCard({ item, onApply }) {
  const Icon = ICON_BY_KIND[item.icon] || Zap;
  return (
    <article
      data-testid={`ready-card-${item.id}`}
      className="bg-[#0F0F0F] p-4 flex flex-col gap-3 hover:bg-[#121212] transition-colors"
    >
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: 'rgba(255,77,148,0.15)', border: '1px solid rgba(255,77,148,0.35)' }}>
          <Icon className="h-4 w-4 text-[#FF4D94]" />
        </div>
        <div className="min-w-0">
          <h4 className="text-[13px] font-black text-white leading-tight">{item.title}</h4>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">{item.goal}</p>
        </div>
      </div>

      <p className="text-[11px] text-gray-400 leading-relaxed">{item.thesis}</p>

      <div className="grid grid-cols-3 gap-2 rounded-lg p-2 bg-[#0A0A0A] border border-[#1f1f1f]">
        <MiniStat label="Creators" value={item.creator_count} />
        <MiniStat label="Reach"    value={item.projected_reach} color="#00D4FF" />
        <MiniStat label="ROAS"     value={item.projected_roas}  color="#4ade80" />
      </div>

      <button
        type="button"
        onClick={() => onApply?.(item.preset, item)}
        data-testid={`ready-apply-${item.id}`}
        className="mt-auto inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-[#FF4D94]/15 border border-[#FF4D94]/40 text-[#FF4D94] text-[11px] font-bold hover:bg-[#FF4D94]/25 transition-colors"
      >
        Use this preset <ChevronRight className="h-3 w-3" />
      </button>
    </article>
  );
}

function MiniStat({ label, value, color = '#ffffff' }) {
  return (
    <div className="text-center">
      <p className="text-[8.5px] uppercase tracking-[0.15em] text-gray-500 font-bold">{label}</p>
      <p className="text-sm font-black tabular-nums leading-tight" style={{ color }}>{value}</p>
    </div>
  );
}
